import { buildingVisibility, visibilityAtLevel } from './buildingVisibility.js';
import { ceilingCameraPose } from './ceilingMounting.js';
import { ceilingMountKind, poleMountKind } from './cameraMounts.js';
import { isPoleObject } from './poles.js';
import { poleCameraPose } from './poleMounting.js';
import { buildGrid } from './coverage.js';
import { polyCentroid, insideSite } from './geometry.js';
import { taskDemands } from './tasks.js';
import { DEG, norm360 } from './constants.js';

/** Only physical roof undersides or upper slabs can supply ceiling candidates.
 * The nominal wall height is never used to invent a ceiling in open space. */
export function ceilingPositions(S, model, entries = [], cfg = {}) {
  const mountKind = ceilingMountKind(model, cfg.ceilingSuspended);
  if (!mountKind) return [];
  const context = buildingVisibility(S), walls = visibilityAtLevel(context, S.elevation || 0).walls;
  const site = { ...S, _mountWalls: walls }, grid = buildGrid(S, 2.5, 0.5, 120), out = [];
  if (!grid) return out;
  const targets = [...S.areas.filter(a => a.kind === 'floor').map(a => ({ ...polyCentroid(a.pts), kind: 'area', targetH: S.opt.hFace })),
    ...S.zones.map(z => ({ ...polyCentroid(z.pts), kind: 'zone', targetH: z.req === 'plate' ? S.opt.hPlate : S.opt.hFace })),
    ...entries.map(e => ({ x: e.x, y: e.y, kind: 'entry', targetH: S.opt.hFace })),
    ...taskDemands(S).map(d => ({ ...d.points[Math.floor(d.points.length / 2)], kind: 'task', taskId: d.taskId, taskReq: d.req, targetH: d.points[0].height }))];
  const points = new Map();
  const add = (x, y) => { if (insideSite(S, x, y)) points.set(`${x.toFixed(3)}:${y.toFixed(3)}`, { x: +x.toFixed(3), y: +y.toFixed(3) }); };
  for (let i = 0; i < grid.n; i++) if (grid.inSite[i]) add(grid.sx[i], grid.sy[i]);
  // A centre-only lattice misses the useful corners of small rooms. Sample
  // both sides of actual underside edges; the support resolver rejects the
  // outside face, openings and insufficient bracket clearance.
  for (const w of walls) if (w.roofPts && w.bottom > 0.3) for (let i = 0; i < w.roofPts.length; i++) {
    const a = w.roofPts[i], b = w.roofPts[(i + 1) % w.roofPts.length], length = Math.hypot(b.x - a.x, b.y - a.y);
    if (length < 0.5) continue;
    const ux = (b.x - a.x) / length, uy = (b.y - a.y) / length, n = Math.max(1, Math.ceil((length - 0.44) / 2.5));
    for (let j = 0; j <= n; j++) for (const side of [-1, 1]) {
      const along = 0.22 + (length - 0.44) * j / n;
      add(a.x + ux * along - uy * 0.22 * side, a.y + uy * along + ux * 0.22 * side);
    }
  }
  for (const point of points.values()) {
    const pose = ceilingCameraPose(site, { modelId: model.id, ...point, pan: 0, tilt: 30, mountKind }, model, { context, lensHeight: cfg.ceilingSuspended ? cfg.ceilingHeight ?? 3 : undefined });
    if (!pose) continue;
    for (const pan of [0, 45, 90, 135, 180, 225, 270, 315]) out.push({ ...pose, pan, kind: 'ceiling' });
    for (const t of targets) out.push({ ...pose, ...t, x: pose.x, y: pose.y, pan: norm360(Math.atan2(t.y - pose.y, t.x - pose.x) * DEG), aimDistance: Math.hypot(t.x - pose.x, t.y - pose.y) });
  }
  return out;
}

/** The search cannot invent posts: every candidate references a user-placed
 * pole and passes the same physical attachment checks as manual placement. */
export function polePositions(S, model, entries = [], cfg = {}) {
  const mountKind = poleMountKind(model);
  if (!mountKind) return [];
  const targets = [...S.areas.filter(a => a.kind === 'floor').map(a => ({ ...polyCentroid(a.pts), kind: 'area', targetH: S.opt.hFace })),
    ...S.zones.map(z => ({ ...polyCentroid(z.pts), kind: 'zone', targetH: z.req === 'plate' ? S.opt.hPlate : S.opt.hFace })),
    ...entries.map(e => ({ x: e.x, y: e.y, kind: 'entry', targetH: S.opt.hFace })),
    ...taskDemands(S).map(d => ({ ...d.points[Math.floor(d.points.length / 2)], kind: 'task', taskId: d.taskId, taskReq: d.req, targetH: d.points[0].height }))];
  const out = [];
  for (const pole of S.obstacles.filter(isPoleObject)) {
    const bearings = new Set([0, 45, 90, 135, 180, 225, 270, 315]);
    for (const t of targets) bearings.add(+norm360(Math.atan2(t.y - pole.y, t.x - pole.x) * DEG).toFixed(1));
    for (const mountAzimuth of bearings) {
      const pose = poleCameraPose(S, { modelId: model.id, mountPoleId: pole.id, mountKind, mountAzimuth, pan: mountAzimuth, h: cfg.height ?? 2.8, tilt: 20 }, model);
      if (!pose) continue;
      out.push({ ...pose, kind: 'pole' });
      for (const t of targets) {
        const cam = { ...pose, ...t, x: pose.x, y: pose.y, pan: norm360(Math.atan2(t.y - pose.y, t.x - pose.x) * DEG), aimDistance: Math.hypot(t.x - pose.x, t.y - pose.y) };
        if (poleCameraPose(S, cam, model)) out.push(cam);
      }
    }
  }
  return out;
}
